import type {
  ConversationDetail,
  ConversationFilter,
  ConversationListItem,
  ConversationListResponse,
} from "@esi/shared";
import {
  type QueryClient,
  queryOptions,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { modelOptionKeys } from "../models/queries.ts";
import { refreshArtefacts } from "./artefacts/queries.ts";

/**
 * Query keys of conversations everywhere (sidebar, lists, search, the open conversation). Lists
 * share one prefix, so a turn end, a rename, a share or a move refreshes every list at once.
 */
export const conversationKeys = {
  lists: ["conversations"] as const,
  list: (query: ConversationListParams) => ["conversations", "list", query] as const,
  shares: (id: string) => ["conversations", "shares", id] as const,
  shareCandidates: (id: string, q: string) => ["conversations", "share-candidates", id, q] as const,
  projectShare: (id: string) => ["conversations", "project-share", id] as const,
  // Outside the list prefix: refreshing the lists never refetches under an open thread.
  detail: (id: string) => ["conversation", id] as const,
};

export interface ConversationListParams {
  projectId?: string | undefined;
  q?: string | undefined;
  filter?: ConversationFilter | undefined;
  limit?: number | undefined;
  /**
   * List through the project's endpoint (its Conversations tab), which includes the audited
   * break-glass listing; the sidebar and cross-project lists never do.
   */
  viaProject?: boolean | undefined;
}

async function fetchPage(
  params: ConversationListParams,
  cursor: string | undefined,
): Promise<ConversationListResponse> {
  if (params.projectId && params.viaProject) {
    // A project's own list: for a break-glass admin, every conversation (audited server-side).
    const page = await ok(
      await api.api.projects[":projectId"].conversations.$get({
        param: { projectId: params.projectId },
        query: {
          ...(params.q ? { q: params.q } : {}),
          ...(params.filter && params.filter !== "all" ? { filter: params.filter } : {}),
          limit: String(params.limit ?? 25),
          ...(cursor ? { cursor } : {}),
        },
      }),
    );
    // Always paged: the query carries `limit`.
    return Array.isArray(page) ? { items: page, nextCursor: null } : page;
  }
  return ok(
    await api.api.me.conversations.$get({
      query: {
        ...(params.projectId ? { projectId: params.projectId } : {}),
        ...(params.q ? { q: params.q } : {}),
        ...(params.filter && params.filter !== "all" ? { filter: params.filter } : {}),
        limit: String(params.limit ?? 25),
        ...(cursor ? { cursor } : {}),
      },
    }),
  );
}

/** One page of the caller's conversations (sidebar sections, the search dialog). */
export function useConversationPage(
  params: ConversationListParams,
  options: { enabled?: boolean; staleTime?: number } = {},
) {
  return useQuery({
    queryKey: conversationKeys.list(params),
    enabled: options.enabled ?? true,
    staleTime: options.staleTime ?? 30_000,
    queryFn: () => fetchPage(params, undefined),
  });
}

/** Every page, loaded on demand ("Load more"): the Conversations page and a project's tab. */
export function useConversationPages(params: ConversationListParams) {
  return useInfiniteQuery({
    queryKey: [...conversationKeys.list(params), "pages"] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => fetchPage(params, pageParam),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** The conversation as the caller may see it (header, history). */
export function conversationQuery(id: string) {
  return queryOptions({
    queryKey: conversationKeys.detail(id),
    // Opened afresh each visit: access, titles and hidden messages follow current rights.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async (): Promise<ConversationDetail> =>
      ok(await api.api.conversations[":conversationId"].$get({ param: { conversationId: id } })),
  });
}

export function useConversation(id: string) {
  return useQuery(conversationQuery(id));
}

/**
 * Refreshes the lists, and the header of `id` (its title is written during the first turn) and
 * its picker options (the turn stored the picker's choice: reopening must not start from a stale
 * cached one and send it back), and its artefacts.
 */
export function refreshConversations(queryClient: QueryClient, id?: string) {
  void queryClient.invalidateQueries({ queryKey: conversationKeys.lists });
  if (id) {
    void queryClient.invalidateQueries({ queryKey: conversationKeys.detail(id), exact: true });
    void queryClient.invalidateQueries({
      queryKey: modelOptionKeys.of({ conversationId: id }),
      exact: true,
    });
    // The turn may have created or revised artefacts (artefacts-viewer).
    refreshArtefacts(queryClient, id);
  }
}

export function useRefreshConversations() {
  const queryClient = useQueryClient();
  return (id?: string) => refreshConversations(queryClient, id);
}

export type { ConversationListItem };
